import React, { useEffect, useMemo, useState } from 'react';
import { adminApi } from '../../api/insolita';
import { SiteContent } from '../../api/types';

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:8082/api').replace(/\/api\/?$/, '');

/** Risolve un URL immagine: assoluto→così com'è, /img→dal FE, altro→dal backend. */
function resolveImg(value: string): string {
  if (!value) return '';
  if (/^https?:\/\//.test(value) || value.startsWith('/img/')) return value;
  return API_ORIGIN + value;
}

const SECTION_LABELS: Record<string, string> = {
  home: 'Home', gare: 'Gare', storia: 'Storia', tour: 'Tour',
  iscrizioni: 'Iscrizioni', contatti: 'Contatti', legale: 'Legale', social: 'Social', brand: 'Brand', altro: 'Altro',
};
const SECTION_ORDER = ['home', 'gare', 'storia', 'tour', 'iscrizioni', 'contatti', 'legale', 'social', 'brand', 'altro'];

const ContentAdmin: React.FC = () => {
  const [items, setItems] = useState<SiteContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [draftsEn, setDraftsEn] = useState<Record<number, string>>({});
  const [active, setActive] = useState<string>('');

  const load = () => {
    setLoading(true);
    adminApi.content.list()
      .then((data) => { setItems(data); setDrafts({}); setDraftsEn({}); })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const bySection = useMemo(() => {
    const map: Record<string, SiteContent[]> = {};
    items.forEach((c) => {
      const s = c.section || 'altro';
      (map[s] = map[s] || []).push(c);
    });
    return map;
  }, [items]);

  const sections = useMemo(() => {
    const present = Object.keys(bySection);
    const ordered = SECTION_ORDER.filter((s) => present.includes(s));
    const extras = present.filter((s) => !SECTION_ORDER.includes(s));
    return [...ordered, ...extras];
  }, [bySection]);

  useEffect(() => {
    if (sections.length && !sections.includes(active)) setActive(sections[0]);
  }, [sections, active]);

  const saveText = async (c: SiteContent) => {
    setSavingId(c.id);
    try {
      await adminApi.content.updateValue(c.id, drafts[c.id] ?? c.value ?? '', draftsEn[c.id] ?? c.valueEn ?? '');
      load();
    } finally { setSavingId(null); }
  };

  const uploadImage = async (c: SiteContent, file: File) => {
    setSavingId(c.id);
    try {
      await adminApi.content.uploadImage(c.contentKey, file, c.label, c.section);
      load();
    } finally { setSavingId(null); }
  };

  if (loading) return <p className="text-neutral-500">Caricamento…</p>;

  return (
    <div>
      <h1 className="text-2xl font-bold mb-1">Contenuti & Immagini</h1>
      <p className="text-neutral-500 mb-6 text-sm">Modifica testi e immagini del sito. Le immagini caricate vengono convertite in WebP e servite dal backend.</p>

      {/* Menu sezioni */}
      <div className="flex flex-wrap gap-2 mb-6 border-b pb-3">
        {sections.map((s) => (
          <button key={s} onClick={() => setActive(s)}
            className={`px-3 py-1.5 rounded text-sm transition ${active === s ? 'bg-[#0B0B0C] text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`}>
            {SECTION_LABELS[s] || s}
          </button>
        ))}
      </div>

      {([[active, bySection[active] || []]] as [string, SiteContent[]][]).map(([section, list]) => (
        <section key={section} className="mb-8">
          <h2 className="text-xs uppercase tracking-widest text-neutral-400 mb-3 border-b pb-2">{SECTION_LABELS[section] || section}</h2>
          <div className="space-y-4">
            {list.map((c) => (
              <div key={c.id} className="bg-white rounded-lg border border-neutral-200 p-4">
                <div className="flex justify-between items-start gap-4">
                  <div className="min-w-0">
                    <p className="font-medium text-sm">{c.label || c.contentKey}</p>
                    <p className="text-[11px] text-neutral-400 font-mono">{c.contentKey}</p>
                  </div>
                  <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-neutral-100 text-neutral-500">{c.type}</span>
                </div>

                {c.type === 'image' ? (
                  <div className="mt-3 flex items-center gap-4">
                    {c.value && <img src={resolveImg(c.value)} alt={c.label} className="h-16 w-24 object-contain bg-neutral-50 border rounded" />}
                    <label className="text-sm px-3 py-2 bg-[#0B0B0C] text-white rounded cursor-pointer hover:opacity-90">
                      {savingId === c.id ? 'Caricamento…' : 'Cambia immagine'}
                      <input type="file" accept="image/*" hidden
                        onChange={(e) => e.target.files?.[0] && uploadImage(c, e.target.files[0])} />
                    </label>
                  </div>
                ) : (
                  <div className="mt-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[10px] uppercase tracking-wide text-neutral-400 mb-1">Italiano</label>
                        <textarea
                          className="w-full border border-neutral-200 rounded p-2 text-sm min-h-[70px]"
                          value={drafts[c.id] ?? c.value ?? ''}
                          onChange={(e) => setDrafts((d) => ({ ...d, [c.id]: e.target.value }))} />
                      </div>
                      <div>
                        <label className="block text-[10px] uppercase tracking-wide text-neutral-400 mb-1">English <span className="text-neutral-300 normal-case">— se vuoto usa l'italiano</span></label>
                        <textarea
                          className="w-full border border-neutral-200 rounded p-2 text-sm min-h-[70px] bg-neutral-50"
                          value={draftsEn[c.id] ?? c.valueEn ?? ''}
                          onChange={(e) => setDraftsEn((d) => ({ ...d, [c.id]: e.target.value }))} />
                      </div>
                    </div>
                    <button onClick={() => saveText(c)} disabled={savingId === c.id}
                      className="mt-2 text-sm px-3 py-1.5 bg-[#E4FF1A] text-[#0B0B0C] font-medium rounded disabled:opacity-60">
                      {savingId === c.id ? 'Salvataggio…' : 'Salva'}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

export default ContentAdmin;
